import type { RefObject } from "react";
import { useEffect } from "react";
import { noop } from "lodash";
import {
  selectActions,
  selectChatRoom,
  useChatRoomStoreMessages,
} from "@/react/hooks/chatsV2/store";
import { useEmbeddableChatApi } from "@/react/hooks/embeddable_chat/useEmbeddableChatsApi";
import { useWebSocket } from "@/react/hooks/useWebSocket";
import { useCurrentCommunityMember } from "@circle-react/contexts";
import {
  useChatMessageReaction,
  useChatMessagesV2Api,
  useChatStore,
} from "@circle-react/hooks/chatsV2";
import { useChatsV2Api } from "@circle-react/hooks/chatsV2/useChatsV2Api";
import { EmptyChatRoom } from "../ChatWindowV2/EmptyChatRoom";
import { RichTextMessageBox } from "../sharedV2";
import { EmbeddableMessagesContainer } from "./EmbeddableMessagesContainer";
import { useRefetchMessagesOnReconnection } from "./useRefetchMessagesOnReconnection";

interface EmbeddableChatProps {
  chatUserSearchRef?: RefObject<HTMLInputElement>;
  displayDayChange?: boolean;
  isChatPreview?: boolean;
  isChatSpace?: boolean;
  isDrawer?: boolean;
  isInCourseSpace?: boolean;
  isLiveStream?: boolean;
  isMessaging?: boolean;
  messageBodyClassName?: string;
  onNewChatMessage?: (message: any) => void;
  roles?: Record<string, any>;
  userList?: any[];
  uuid: string;
}

export const EmbeddableChat = ({
  uuid,
  displayDayChange = true,
  onNewChatMessage,
  isChatSpace = false,
  chatUserSearchRef,
  isLiveStream = false,
  isInCourseSpace = false,
  isDrawer = false,
  roles = {},
  messageBodyClassName = "",
  isChatPreview,
  userList = [],
  isMessaging = false,
}: EmbeddableChatProps) => {
  const { onEventReceive: onMessagesEventReceive } = useChatMessagesV2Api();
  const { chatRoomMessages } = useChatRoomStoreMessages();
  const { onReactionWebsocketEvent } = useChatMessageReaction();
  useRefetchMessagesOnReconnection({ uuid, isLiveStream });
  const currentCommunityMember = useCurrentCommunityMember();

  const { onEventReceive: onChatEventReceive, onEmbedEventReceive }: any =
    useChatsV2Api();
  const {
    chatRoom,
    onEventReceive: onEmbeddedEventReceive,
    isLoading: isChatRoomLoading,
  } = useEmbeddableChatApi(uuid);
  const currentEmbeddedChatRoom = useChatStore(selectChatRoom(uuid));
  const { appendChatRoom } = useChatStore(selectActions);

  useEffect(() => {
    if (chatRoom && !currentEmbeddedChatRoom) {
      appendChatRoom(chatRoom);
    }
  }, [appendChatRoom, chatRoom, currentEmbeddedChatRoom]);

  useWebSocket(
    {
      channel: "Chats::RoomChannel",
      chat_room_uuid: chatRoom?.uuid,
      onMessageReceive: (data: any) => {
        if (data.event === "newMessage") {
          onChatEventReceive(
            data,
            currentCommunityMember?.id,
            uuid === chatRoom?.uuid,
          );
          if (onNewChatMessage && data?.json_message) {
            onNewChatMessage(data.json_message);
          }
        }
        onMessagesEventReceive(data, chatRoomMessages);
        onEmbeddedEventReceive(data);
      },
      canCreateConnection: Boolean(
        chatRoom?.uuid && currentCommunityMember?.id,
      ),
    },
    [chatRoom?.uuid, currentCommunityMember?.id],
  );

  useWebSocket(
    {
      channel: "ChatRoomChannel",
      onMessageReceive: (data: any) => {
        onReactionWebsocketEvent(data);
        onEmbedEventReceive(data);
      },
      community_member_id: currentCommunityMember?.id,
      canCreateConnection: chatRoom?.uuid && currentCommunityMember?.id,
    },
    [chatRoom?.uuid, currentCommunityMember?.id],
  );

  if (!currentEmbeddedChatRoom) {
    if (!isMessaging) {
      return null;
    }
    if (uuid === "new" && (userList.length === 0 || isChatRoomLoading)) {
      return <RichTextMessageBox onClick={noop} disabled />;
    }
    return <EmptyChatRoom userList={userList} />;
  }

  return (
    <EmbeddableMessagesContainer
      chatRoom={currentEmbeddedChatRoom}
      displayDayChange={displayDayChange}
      isChatSpace={isChatSpace}
      chatUserSearchRef={chatUserSearchRef}
      isLiveStream={isLiveStream}
      isInCourseSpace={isInCourseSpace}
      isDrawer={isDrawer}
      roles={roles}
      messageBodyClassName={messageBodyClassName}
      isChatPreview={isChatPreview}
    />
  );
};
